import React, { useMemo } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";

/**
 * CloudLayer
 * Soft purple clouds drifting right -> left across the screen.
 *
 * Props:
 *  - rows?: number           (how many "lanes", default 3)
 *  - minSpeed?: number       (seconds across, default 20)
 *  - maxSpeed?: number       (seconds across, default 40)
 *  - density?: number        (clouds per lane, default 2)
 *  - zIndex?: number         (default 0)
 */
export default function CloudLayer({
  rows = 3,
  minSpeed = 20,
  maxSpeed = 40,
  density = 2,
  zIndex = 0,
}) {
  const theme = useTheme();
  const shouldReduceMotion = useReducedMotion();
  const isMobile = useMediaQuery("(max-width:900px)");

  // Create a small SVG cloud path that matches the reference style.
  // (rounded pill with a bump)
  const CloudSVG = ({ scale = 1, opacity = 0.5 }) => (
    <svg
      width={96 * scale}
      height={56 * scale}
      viewBox="0 0 96 56"
      aria-hidden
      style={{ display: "block" }}
    >
      <path
        d="M18 36c-6.6 0-12-5.4-12-12S11.4 12 18 12c2.4 0 4.6.7 6.5 1.9C27.5 9.7 33.4 6 40.2 6c8.3 0 15.4 5.3 18 12.7C60 18.2 62.1 18 64.2 18 75.5 18 84.7 27.3 84.7 38.5S75.5 59 64.2 59H25.8C12.9 59 2.5 48.6 2.5 35.7 2.5 35.7 10 36 18 36z"
        fill={theme.palette.mode === "dark" ? "#9b6cff" : "#a07bff"}
        fillOpacity={opacity}
      />
    </svg>
  );

  // Build cloud instances (row, size, y, speed, delay)
  const clouds = useMemo(() => {
    const total = rows * density;
    return Array.from({ length: total }, (_, i) => {
      const row = i % rows;
      //   const y = 10 + row * (80 / (rows - 1 || 1)); // percentage offset
      const topStart = 5; // % from the top
      const topEnd = 45; // % from the top (<= 50 to stay in top half)
      const y = topStart + row * ((topEnd - topStart) / (rows - 1 || 1));
      const scale = isMobile
        ? 0.7 + Math.random() * 0.8
        : 0.9 + Math.random() * 1;
      const opacity = 0.35 + Math.random() * 0.25;
      const speed = minSpeed + Math.random() * Math.max(1, maxSpeed - minSpeed);
      const delay = Math.random() * speed; // stagger looping
      return { id: i, y, scale, speed, delay, opacity };
    });
  }, [rows, density, minSpeed, maxSpeed]);

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex,
      }}
    >
      {clouds.map((c) => (
        <motion.div
          key={c.id}
          style={{
            position: "absolute",
            top: `${c.y}%`,
            left: shouldReduceMotion ? "50%" : "110%",
            translateY: "-50%",
          }}
          animate={
            shouldReduceMotion ? undefined : { x: ["0vw", "-220vw"] } // start off-screen right -> out left
          }
          transition={
            shouldReduceMotion
              ? undefined
              : {
                  duration: c.speed,
                  delay: c.delay,
                  repeat: Infinity,
                  ease: "linear",
                }
          }
        >
          <Box sx={{ transform: `scale(${c.scale})` }}>
            <CloudSVG scale={c.scale} opacity={c.opacity} />
          </Box>
        </motion.div>
      ))}
    </Box>
  );
}
